import React, { useState, useEffect } from "react"
import { toast } from "react-toastify"
import CommonSelect from "../../../core/common/commonSelect"
import StudentDropdown from "../../../core/common/StudentDropdown"
import StaffDropdown from "../../../core/common/StaffDropdown"
import { SelectOption } from "../../../core/common/InfiniteSelectDropdown"
import { CreateBookIssuePayload, issueBook } from "../../../service/book-issue"
import {
  getAllBooks,
  getBookItems,
  Book,
  BookItem,
} from "../../../service/books"
import { RECIPIENT_TYPE_OPTIONS, RECIPIENT_TYPES } from "../../../constant"

interface IssueBookModalProps {
  onIssueSuccess?: () => void
}

interface BookOption {
  value: string
  label: string
  author: string
  available_quantity: number
  isbn: string
}

interface BookItemOption {
  value: string
  label: string
  status: string
  barcode?: string
}
const getTodayDate = () => new Date().toISOString().split("T")[0]

const DEFAULT_FORM_DATA = {
  recipient_type: RECIPIENT_TYPES.STUDENT,
  userId: "",
  book: "",
  bookItemId: "",
  due_date: "",
  perDayOverdueFine: 0,
  remarks: "",
  issue_date: getTodayDate(),
}

const IssueBookModal: React.FC<IssueBookModalProps> = ({
  onIssueSuccess = () => {},
}) => {
  const [loading, setLoading] = useState(false)
  const [books, setBooks] = useState<BookOption[]>([])
  const [booksLoading, setBooksLoading] = useState(false)
  const [bookItems, setBookItems] = useState<BookItemOption[]>([])
  const [bookItemsLoading, setBookItemsLoading] = useState(false)
  const [selectedRecipient, setSelectedRecipient] = useState<SelectOption>()
  const [formData, setFormData] =
    useState<CreateBookIssuePayload>(DEFAULT_FORM_DATA)

  useEffect(() => {
    fetchAvailableBooks()
  }, [])

  const fetchAvailableBooks = async () => {
    try {
      setBooksLoading(true)

      const response = await getAllBooks({})

      if (response.success) {
        const bookOptions: BookOption[] = response.data.map((book: Book) => ({
          value: book._id,
          label: `${book.title} by ${book.author}`,
          author: book.author,
          available_quantity: parseInt(
            book.available_quantity?.toString() || "0",
          ),
          isbn: book.isbn || "N/A",
        }))

        setBooks(bookOptions)
      } else {
        toast.error("Failed to load books list")
        setBooks([])
      }
    } catch (error) {
      toast.error("Failed to load books list")
      setBooks([])
    } finally {
      setBooksLoading(false)
    }
  }

  const handleRecipientTypeChange = (selectedOption: any) => {
    const recipientType = selectedOption.value

    setSelectedRecipient(undefined)
    setFormData(prev => ({
      ...prev,
      recipient_type: recipientType,
      recipientId: "",
    }))
  }

  const handleRecipientChange = (selected: SelectOption) => {
    setFormData(prev => ({
      ...prev,
      userId: selected.value,
    }))
    setSelectedRecipient(selected)
  }

  const handleBookChange = (selectedOption: any) => {
    setFormData(prev => ({
      ...prev,
      book: selectedOption.value,
      bookItemId: "",
    }))
    const bookId = selectedOption.value
    if (bookId) {
      fetchBookItems(bookId)
    }
  }

  const fetchBookItems = async (bookId: string) => {
    try {
      setBookItemsLoading(true)
      // backend paginates items; request a larger page size for dropdown
      const res = await getBookItems(bookId, {
        status: "available",
        page: 1,
        limit: 100,
      })
      if (res?.success) {
        const options: BookItemOption[] = (res.data || []).map(
          (item: BookItem) => ({
            value: item._id,
            label: `${item.copyIdentifier}${item.barcode ? ` (${item.barcode})` : ""}`,
            status: item.status,
            barcode: item.barcode,
          }),
        )
        setBookItems(options)
      } else {
        setBookItems([])
      }
    } catch (err) {
      setBookItems([])
    } finally {
      setBookItemsLoading(false)
    }
  }

  const handleBookItemChange = (selectedOption: any) => {
    setFormData(prev => ({
      ...prev,
      bookItemId: selectedOption?.value || "",
    }))
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const resetForm = () => {
    setFormData(DEFAULT_FORM_DATA)
    setBookItems([])
    setSelectedRecipient(undefined)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (loading) return

    // Validation
    if (
      !formData.recipient_type ||
      !formData.userId ||
      !formData.book ||
      !formData.bookItemId ||
      !formData.due_date ||
      !formData.issue_date
    ) {
      toast.error("Please fill all required fields")
      return
    }

    const issueDate = formData.issue_date

    if (new Date(formData.due_date) <= new Date(issueDate)) {
      toast.error("Due date must be after issue date")
      return
    }

    if (selectedBook && selectedBook.available_quantity <= 0) {
      toast.error("Selected book is not available for issue")
      return
    }

    if (bookItems.length === 0) {
      toast.error("No available copies found for the selected book")
      return
    }

    setLoading(true)

    try {
      const response = await issueBook({
        book: formData.book,
        bookItemId: formData.bookItemId,
        due_date: formData.due_date,
        issue_date: formData.issue_date,
        perDayOverdueFine: Number(formData.perDayOverdueFine),
        recipient_type: formData.recipient_type,
        remarks: formData.remarks,
        userId: formData.userId,
      })

      if (response.success) {
        toast.success("Book issued successfully!")
        onIssueSuccess()
        closeModal("issue_book_modal")
        resetForm()
      } else {
        toast.error("Failed to issue book")
      }
    } catch (error: any) {
      toast.error(error.message || "Error issuing book")
    } finally {
      setLoading(false)
    }
  }

  const closeModal = (modalId: string) => {
    const el = document.getElementById(modalId)
    if (el) {
      el.setAttribute("data-bs-dismiss", "modal")
      el.click()
      el.removeAttribute("data-bs-dismiss")
    }
  }

  const getTomorrowDate = () => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return tomorrow.toISOString().split("T")[0]
  }

  const getMaxDate = () => {
    const maxDate = new Date()
    maxDate.setFullYear(maxDate.getFullYear() + 1)
    return maxDate.toISOString().split("T")[0]
  }
  const selectedBook = books.find(book => book.value === formData.book)
  const selectedBookItem = bookItems.find(
    option => option.value === formData.bookItemId,
  )
  return (
    <div className="modal fade" id="issue_book_modal">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">Issue Book</h4>
            <button
              type="button"
              className="btn-close custom-btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={resetForm}
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Recipient Type *</label>
                    <CommonSelect
                      className="select"
                      isDisabled
                      options={RECIPIENT_TYPE_OPTIONS}
                      value={RECIPIENT_TYPE_OPTIONS.find(
                        option => option.value === formData.recipient_type,
                      )}
                      onChange={handleRecipientTypeChange}
                      placeholder="Select recipient type"
                      required={true}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Recipient *</label>
                    {formData.recipient_type === RECIPIENT_TYPES.STUDENT ? (
                      <StudentDropdown
                        value={selectedRecipient}
                        onChange={handleRecipientChange}
                        placeholder="Search and select student..."
                        isMulti={false}
                        className="student-dropdown"
                        limit={10}
                      />
                    ) : formData.recipient_type === RECIPIENT_TYPES.STAFF ? (
                      <StaffDropdown
                        value={selectedRecipient}
                        onChange={handleRecipientChange}
                        placeholder="Search and select staff..."
                        isMulti={false}
                        className="staff-dropdown"
                        limit={10}
                        filters={{ status: "active" }}
                      />
                    ) : (
                      <div className="text-muted small">
                        Select recipient type first
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="form-label">Select Book *</label>
                    <CommonSelect
                      className="select"
                      options={books}
                      value={books.find(
                        option => option.value === formData.book,
                      )}
                      isSearchable={true}
                      onChange={handleBookChange}
                      required={true}
                      isDisabled={booksLoading}
                      placeholder={
                        booksLoading ? "Loading books..." : "Select book"
                      }
                    />
                    {selectedBook && (
                      <div className="mt-1">
                        <small className="text-muted">
                          <strong>Selected:</strong> {selectedBook.label}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="form-label">Select Book Copy *</label>
                    <CommonSelect
                      className="select"
                      options={bookItems}
                      value={bookItems.find(
                        option => option.value === formData.bookItemId,
                      )}
                      isSearchable={true}
                      onChange={handleBookItemChange}
                      required={true}
                      isDisabled={!formData.book || bookItemsLoading}
                      placeholder={
                        !formData.book
                          ? "Select book first"
                          : bookItemsLoading
                            ? "Loading copies..."
                            : bookItems.length === 0
                              ? "No available copies"
                              : "Select copy identifier"
                      }
                    />
                    {selectedBookItem && (
                      <div className="mt-1">
                        <small className="text-muted">
                          <strong>Selected Copy:</strong>{" "}
                          {selectedBookItem.label}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {selectedBook && selectedBook.available_quantity === 0 && (
                <div className="alert alert-warning mb-3">
                  <i className="ti ti-alert-circle me-2"></i>
                  This book is currently not available for issue.
                </div>
              )}

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Issue Date *</label>
                    <input
                      type="date"
                      className="form-control"
                      name="issue_date"
                      value={formData.issue_date}
                      onChange={handleInputChange}
                      aria-readonly="true"
                      title="Issue date is always today and cannot be changed"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Due Date *</label>
                    <input
                      type="date"
                      className="form-control"
                      name="due_date"
                      value={formData.due_date}
                      onChange={handleInputChange}
                      required
                      min={getTomorrowDate()}
                      max={getMaxDate()}
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">
                      Per Day Overdue Fine (PKR) *
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      name="perDayOverdueFine"
                      step={1}
                      value={formData.perDayOverdueFine}
                      onChange={handleInputChange}
                      placeholder="e.g. 10 (optional)"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-0">
                <label className="form-label">Remarks</label>
                <textarea
                  className="form-control"
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Enter any remarks..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="modal"
                onClick={resetForm}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  loading ||
                  selectedBook?.available_quantity === 0 ||
                  !formData.bookItemId ||
                  bookItemsLoading
                }
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>
                    Issuing...
                  </>
                ) : (
                  "Issue Book"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default IssueBookModal
